'use client'

import { useRef } from 'react'
import { motion, useInView, useReducedMotion } from 'framer-motion'
import { Check } from 'lucide-react'

import { Button, type PackageManager } from '@mdxui/primitives'
import { InstallSnippet } from '@mdxui/primitives/install-snippet'

import { cn } from '../../lib/cn'
import { Markdown } from '../../lib/markdown'
import {
  sectionVariants,
  headerVariants,
  gridItemVariants,
  gridContainerVariants,
  reducedMotionVariants,
} from '../../lib/motion'

/**
 * Shared props for the centered-checklist CTA.
 */
export interface CtaCenteredCheckBaseProps {
  /** Main heading text */
  heading?: string
  /** Optional description text below heading */
  description?: string
  /** List of items to display with checkmarks (recommended: 3 items) */
  items?: string[]
  /** Optional background pattern (rendered inside card at 50% opacity) */
  background?: React.ReactNode
  /** Additional CSS classes */
  className?: string
}

/**
 * Props for CtaCenteredCheck — centered CTA with horizontal checkmark list.
 *
 * @remarks
 * **IMPORTANT CONSTRAINTS FOR AI/PROGRAMMATIC GENERATION:**
 * - Centered layout with heading, optional description, checkmark items in a row
 *   (3 across on desktop, stacked on mobile). Best for 3 items — more may wrap awkwardly.
 * - `ctaMode` selects the call-to-action row:
 *   - `'buttons'` (default): 1-2 regular buttons (primary, optional secondary)
 *   - `'install-snippet'`: copy-to-clipboard install command — **ALWAYS use this for
 *     npm/npx/pnpm/yarn/bun install instructions**
 * - The discriminated union makes mixing buttons with an install snippet impossible.
 */
export type CtaCenteredCheckProps = CtaCenteredCheckBaseProps &
  (
    | {
        ctaMode?: 'buttons'
        /** Primary button configuration */
        primaryButton?: { text: string; url: string }
        /** Optional secondary button configuration */
        secondaryButton?: { text: string; url: string }
      }
    | {
        ctaMode: 'install-snippet'
        /** Package name for the install command (e.g., "mdxui", "@mdxui/cli") */
        package: string
        /** Package manager command prefix. Default: "npx" */
        command?: PackageManager
      }
  )

/**
 * CtaCenteredCheck - Centered CTA with horizontal checkmark list
 *
 * **Layout:**
 * ```
 * ─────────────────────────────────────
 *              Heading
 *            Description
 *    ✓ Item 1  ✓ Item 2  ✓ Item 3
 *    [Button]  /  [npx package 📋]
 * ─────────────────────────────────────
 * ```
 *
 * **Best Use Cases:**
 * - Highlighting key benefits or guarantees with a closing CTA
 * - Trust indicators (MIT licensed, Production ready, etc.)
 * - Developer onboarding / npm quick-start CTAs (`ctaMode="install-snippet"`)
 *
 * @example
 * ```tsx
 * <CtaCenteredCheck
 *   heading="Get Started in Seconds"
 *   items={['Open source', 'MIT licensed', 'Production ready']}
 *   primaryButton={{ text: 'Get Started', url: '/signup' }}
 * />
 * ```
 *
 * @example
 * ```tsx
 * <CtaCenteredCheck
 *   ctaMode="install-snippet"
 *   heading="Install the CLI"
 *   package="@mdxui/cli"
 *   command="npm install"
 * />
 * ```
 */
export function CtaCenteredCheck(props: CtaCenteredCheckProps) {
  const {
    heading = 'Get Started in Seconds',
    description,
    items = ['Open source', 'MIT licensed', 'Production ready'],
    background,
    className,
  } = props

  const sectionRef = useRef<HTMLElement>(null)
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' })
  const prefersReducedMotion = useReducedMotion()

  const contentAnimation = prefersReducedMotion
    ? reducedMotionVariants
    : headerVariants

  const itemAnimation = prefersReducedMotion
    ? reducedMotionVariants
    : gridItemVariants

  const listContainerVariants = prefersReducedMotion
    ? reducedMotionVariants
    : gridContainerVariants(0.1, 0.2)

  const primaryButton =
    props.ctaMode === 'install-snippet'
      ? undefined
      : props.primaryButton ?? { text: 'Get Started', url: '#' }
  const secondaryButton =
    props.ctaMode === 'install-snippet' ? undefined : props.secondaryButton

  return (
    <motion.section
      ref={sectionRef}
      className={cn('py-16 lg:py-32', className)}
      variants={sectionVariants}
      initial="hidden"
      animate={isInView ? 'visible' : 'hidden'}
    >
      <div className="container max-w-7xl mx-auto">
        <div className="relative overflow-hidden flex flex-col items-center justify-center rounded-[var(--radius-xl,calc(var(--radius)*1.5))] border border-border bg-muted/50 py-16 text-center md:py-20">
          {background && (
            <div className="absolute inset-0 opacity-50">{background}</div>
          )}
          <div className="relative z-10 mx-auto max-w-3xl px-4">
            <motion.h2
              className="mb-6 sm:mb-4 text-balance text-3xl font-semibold md:text-5xl"
              variants={contentAnimation}
            >
              {heading}
            </motion.h2>

            {description && (
              <motion.div
                className="mb-6"
                variants={contentAnimation}
              >
                <Markdown size="base">{description}</Markdown>
              </motion.div>
            )}

            {items && items.length > 0 && (
              <motion.ul
                className="mb-8 flex flex-col items-center justify-center gap-4 sm:flex-row sm:gap-8"
                variants={listContainerVariants}
              >
                {items.map((item) => (
                  <motion.li
                    key={item}
                    className="flex items-center gap-2 text-sm font-medium text-muted-foreground"
                    variants={itemAnimation}
                  >
                    <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-primary/10">
                      <Check className="size-3 text-primary" />
                    </span>
                    <span className="whitespace-nowrap">{item}</span>
                  </motion.li>
                ))}
              </motion.ul>
            )}

            {props.ctaMode === 'install-snippet' ? (
              <motion.div
                className="flex justify-center"
                variants={contentAnimation}
              >
                <InstallSnippet
                  package={props.package}
                  command={props.command ?? 'npx'}
                  className="w-full sm:w-auto"
                />
              </motion.div>
            ) : (
              <motion.div
                className="flex flex-col justify-center gap-2 sm:flex-row"
                variants={contentAnimation}
              >
                {primaryButton && (
                  <Button
                    size="lg"
                    asChild
                    className={`w-full sm:w-auto${!secondaryButton ? ' sm:min-w-[280px]' : ''}`}
                  >
                    <a href={primaryButton.url}>{primaryButton.text}</a>
                  </Button>
                )}
                {secondaryButton && (
                  <Button
                    size="lg"
                    variant="outline"
                    asChild
                    className="w-full sm:w-auto"
                  >
                    <a href={secondaryButton.url}>{secondaryButton.text}</a>
                  </Button>
                )}
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </motion.section>
  )
}
